<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useCarsStore } from '~/stores/CarsStore';
import { useFilterStore } from '~/stores/FilterStore';
import { useLocationsStore } from '~/stores/LocationsStore';
import { useNuxtApp, useRoute } from '#app';

interface Payload {
  'from-id': string | null;
  'to-id': string | null;
  'from-date': string | null;
  'to-date': string | null;
  'from-time': string | null;
  'to-time': string | null;
  'promocode': string | null;
  'sort_order'?: string | null;
  'transmissionFilter'?: string | null;
  'yearFilter'?: string | null;
  'engineFilter'?: string | null;
  'seatsFilter'?: string | null;
  'driveTypeFilter'?: string | null;
  'mileageFilter'?: string | null;
  'ratePerDayFilter'?: string | null;
  'engineCapacityFilter'?: string | null;
  'depositFilter'?: string | null;
  'carClassFilters'?: string | null;
  page: number;
  per_page: number;
}

const nuxtApp = useNuxtApp();
const route = useRoute();
const cars = useCarsStore();
const filters = useFilterStore();
const locationsStore = useLocationsStore();

cars.loading = true;
locationsStore.loading = false;

// Инициализация дат и времени
const getDateFromQueryOrStore = (key, defaultOffset) => {
  const queryDate = route.query[key];
  const storeDate = locationsStore.item?.searchParams[key];

  if (queryDate) {
    return nuxtApp.$moment(queryDate, 'DD.MM.YYYY').toDate();
  }
  if (storeDate) {
    return nuxtApp.$moment(storeDate, 'DD.MM.YYYY').toDate();
  }

  const date = new Date();
  date.setDate(date.getDate() + defaultOffset);
  return date;
};

const initializeDates = () => {
  const dateStart = ref<Date>(getDateFromQueryOrStore('from-date', 3));
  const dateEnd = ref<Date>(getDateFromQueryOrStore('to-date', 10));

  return { dateStart, dateEnd };
};

const { dateStart, dateEnd } = initializeDates();

// Инициализация payload
const initializePayload = () => {
  const title = locationsStore?.item?.searchResult?.pick_up_location_name?.seo_title_su ?
    locationsStore?.item?.searchResult?.pick_up_location_name?.seo_title_su :
    (locationsStore.item?.location_name ?
    'Аренда авто в городе ' + locationsStore.item?.location_name + ' - посуточный прокат машин без водителя в городе' + locationsStore.item?.location_name :
    'mycarrental.su');

  useHead({
    title: title,
    meta: [
      {
        name: 'description',
        content: locationsStore?.item?.searchResult?.pick_up_location_name?.seo_description_su ?
          locationsStore?.item?.searchResult?.pick_up_location_name?.seo_description_su :
          (locationsStore.item?.location_name ?
          'Аренда авто в городе ' + locationsStore.item?.location_name + ' без водителя. Прокат машин недорого в городе ' + locationsStore.item?.location_name + ', большой парк машин от эконом до премиум класса. Бронирование круглосуточно' : '')
      },
      {
        name: 'keywords',
        content: locationsStore?.item?.searchResult?.pick_up_location_name?.seo_keywords ?
          locationsStore?.item?.searchResult?.pick_up_location_name?.seo_keywords :
          'Аренда авто в городе ' + locationsStore.item?.location_name + ' без водителя. Прокат машин недорого в городе ' + locationsStore.item?.location_name + ', большой парк машин от эконом до премиум класса. Бронирование круглосуточно'
      },
    ],
  });

  const fromDate = route.query['from-date'] || locationsStore.item?.searchParams['from-date'] || nuxtApp.$moment(dateStart.value).format('DD.MM.YYYY');
  const toDate = route.query['to-date'] || locationsStore.item?.searchParams['to-date'] || nuxtApp.$moment(dateEnd.value).format('DD.MM.YYYY');
  const fromTime = route.query['from-time'] || locationsStore.item?.searchParams['from-time'] || '12:00';
  const toTime = route.query['to-time'] || locationsStore.item?.searchParams['to-time'] || '12:00';

  cars.payload = {
    'from-id': route.query['from-id'] || locationsStore.item?.searchParams['from-id'] || null,
    'to-id': route.query['to-id'] || locationsStore.item?.searchParams['to-id'] || null,
    'from-date': fromDate,
    'to-date': toDate,
    'from-time': fromTime,
    'to-time': toTime,
    'promocode': route.query['promocode'] || locationsStore.item?.searchParams['promocode'] || null,
    page: 1,
    per_page: 24,
  };
};

const initializeData = async () => {
  if (route.query['from-id']) {
    await locationsStore.getItem(route.params.slug as string);
    cars.isReadyPage = false;
  } else {
    cars.list = [];
    await locationsStore.getItemWithCars(route.params.slug as string);
    cars.isReadyPage = true;
  }

  initializePayload();

  if (route.query['from-id'] || locationsStore.item?.searchParams) {
    if (route.query['from-id']) {
      await cars.get(cars.payload);
    } else {
      cars.loading = false;
    }
  } else {
    cars.loading = false;
  }
};

initializeData();

const bookingFormRef = ref();
onMounted(() => {
  if (bookingFormRef.value) {
    bookingFormRef.value.updateDates(dateStart.value, dateEnd.value);
    bookingFormRef.value.updateTimes(cars.payload['from-time'], cars.payload['to-time']);
  }
});

const chooseSort = async (item: string) => {
  cars.loading = true;
  cars.list = [];
  cars.isReadyPage = false;
  cars.payload.sort_order = item;
  await cars.get(cars.payload);
  cars.loading = false;
};

const chooseFilters = async (filterParams: any) => {
  if (window.innerWidth < 761) {
    nuxtApp.$enablePageScroll();
  }
  cars.isReadyPage = false;
  cars.loading = true;
  cars.list = [];

  cars.payload.transmissionFilter = filterParams.transmissionFilter || 'any';
  cars.payload.yearFilter = filterParams.yearFilter || 'any';
  cars.payload.engineFilter = filterParams.engineFilter || 'any';
  cars.payload.seatsFilter = filterParams.seatsFilter || 'any';
  cars.payload.driveTypeFilter = filterParams.driveTypeFilter || 'any';
  cars.payload.mileageFilter = filterParams.mileageFilter || 'any';

  if (filterParams.ratePerDayFilter.price1 && filterParams.ratePerDayFilter.price2) {
    cars.payload.ratePerDayFilter = `${filterParams.ratePerDayFilter.price1}:${filterParams.ratePerDayFilter.price2}`;
  } else {
    cars.payload.ratePerDayFilter = 'any';
  }

  if (filterParams.depositFilter.price1 && filterParams.depositFilter.price2) {
    cars.payload.depositFilter = `${filterParams.depositFilter.price1}:${filterParams.depositFilter.price2}`;
  } else {
    cars.payload.depositFilter = 'any';
  }

  if (filterParams.engineCapacityFilter.value1 && filterParams.engineCapacityFilter.value2) {
    cars.payload.engineCapacityFilter = `${filterParams.engineCapacityFilter.value1}:${filterParams.engineCapacityFilter.value2}`;
  } else {
    cars.payload.engineCapacityFilter = 'any';
  }

  await cars.get(cars.payload);
  cars.loading = false;
};

const chooseClass = async (id: number) => {
  cars.loading = true;
  await cars.clearList();

  if (cars.activeClassId === id) {
    delete cars.payload.carClassFilters;
    cars.setClassId(0);
  } else {
    const classParams: any = {};
    classParams[id] = true;
    cars.payload.carClassFilters = JSON.stringify(classParams);
    cars.setClassId(id);
  }

  await cars.get(cars.payload);
  cars.loading = false;
};

filters.getClassList();

const load = async () => {
  cars.loadingButton = true;
  cars.isReadyPage = false;

  if (cars.list && cars.list.length > 0 && cars.list.length < cars.counter) {
    cars.payload.page++;
    await cars.get(cars.payload);
    cars.loadingButton = false;
  }
};

const details = ref(false);

const handleScroll = () => {
  const scrollTop = window.scrollY;

  if (window.innerWidth < 761) {
    details.value = scrollTop > 400;
  } else {
    details.value = false;
  }
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

const bgImage = computed(() => locationsStore?.item?.searchResult?.pick_up_location_name?.full_page_image_path);

</script>

<template>
    <div>
      <div class="main small">
        <div class="main__head"
             :style="{backgroundImage: bgImage ? `url(${bgImage})` : `linear-gradient(to bottom, #ffffff, #d3d3d3)`}"
        >
          <div class="main__content">
            <div class="container">
              <div class="main__content-wrapper" v-if="locationsStore?.item?.searchResult?.pick_up_location_name?.h1_title_su">
                <h1 class="main__title">
                  {{ locationsStore?.item?.searchResult?.pick_up_location_name?.h1_title_su }}
                </h1>
                <div class="main__title main__title--skeleton" v-show="locationsStore.loading"></div>
              </div>
              <div class="main__content-wrapper" v-else>
                <h1 class="main__title">
                 {{locationsStore?.item?.h1_heading}}
                </h1>
                <div class="main__title main__title--skeleton" v-show="locationsStore.loading"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="content">
        <div class="container">
          <div class="content__wrapper">
            <BookingForm keep-alive/>
            <div class="content__main" id="cars">
              <CarsFilter :counter="cars.counter" :classList="filters.classList" :payload="payload" @chooseClass="chooseClass" @chooseFilters="chooseFilters" @chooseSort="chooseSort" />
              <div class="options">
                <div class="options__wrapper">
                  <div class="options__content" v-show="cars.loading">
                    <CarsItemSceleton v-for="(car, index) in 12" :key="index" :car="car" />
                  </div>
                  <div class="options__content" v-show="!cars.loading">
                    <CarsItem v-for="car in cars.list" :key="car.id" :car="car" :payload="cars.payload" :location="locationsStore.item" />
                    <div class="item-card"></div>
                    <div class="item-card"></div>
                  </div>
                </div>
                <div class="options__button" v-if="cars.list && cars.list.length > 0 && cars.list.length < cars.counter && !cars.isReadyPage">
                  <button v-if="cars.loadingButton" href="javascript:void(0)" disabled class="button button-line button-medium button-loupe">Загружаем...</button>
                  <button v-else href="javascript:void(0)" @click="load" class="button button-line button-medium button-loupe">Показать еще</button>
                </div>
                <div v-if="cars.counter !== null && cars.counter == 0" class="options__button">Все машины заняты, попробуйте поискать на другие даты</div>
                <!-- <InfiniteLoading @infinite="load" v-if="!cars.loading" /> -->
              </div>
              <LocationFAQ v-if="locationsStore.item && locationsStore.item.searchResult && locationsStore.item.searchResult.faqs && locationsStore.item.searchResult.faqs.length > 0" :location="locationsStore" />
              <div v-else class="section faq"></div>
              <FeedbackForm />
              <LocationArticle v-if="locationsStore.item && locationsStore.item !== null && locationsStore.item.searchResult && locationsStore.item.searchResult !== null" :location="locationsStore.item.searchResult.pick_up_location_name"  />
              <LocationMap v-if="locationsStore.item && locationsStore.item.searchResult && cars.counter !== 0" :location="locationsStore.item.searchResult"  />
              <LocationReviews :location="locationsStore" />
              <LocationNews v-if="locationsStore.item && locationsStore.item !== null && locationsStore.item.searchResult && locationsStore.item.searchResult.news && locationsStore.item.searchResult.news.length > 0" :news="locationsStore.item.searchResult.news" :location="locationsStore" />
            </div>
          </div>
        </div>
        <div class="booking-details" v-if="details && cars.list && cars.list.length > 0 && cars.list[0].rent_period_days">
          <p v-if="locationsStore && locationsStore.active && locationsStore.active.name">{{ locationsStore.active.name }}</p>
          <p>{{ $moment(`${cars.payload['from-date']}`, 'DD-MM-YYYY').format('D MMMM') }} — {{ $moment(`${cars.payload['to-date']}`, 'DD-MM-YYYY').format('D MMMM') }}, с {{ cars.payload['from-time'] }} — {{ cars.list[0].rent_period_days }} {{ $getNumberLabels(cars.list[0].rent_period_days, ['день', 'дня', 'дней']) }}</p>
        </div>
      </div>
    </div>
</template>

<style>
.container {
    overflow: visible !important;
}
.item-card:empty {
  padding: 0;
}
</style>